import { useQueryClient } from "@tanstack/react-query";

import { casesKeys } from "src/cases/queries/cases";
import { caseQueuesKeys } from "src/cases/queries/queues";
import type { DeserializedCaseQueue } from "src/cases/queues/types";
import type { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion } from "src/clients/case-management";
import {
  useCopilotContextualEntities,
  useCopilotRefetchOnMutation,
} from "src/unifiedCopilot/context/hooks";
import type { ReferenceContextualEntity } from "src/unifiedCopilot/context/types";

type CaseTemplateSummary = {
  id: string;
  name: string;
};

type CaseQueueSummary = Pick<DeserializedCaseQueue, "id" | "name">;

export const mapCaseTemplateToCopilotContextualEntity = (
  template: CaseTemplateSummary,
): ReferenceContextualEntity => ({
  kind: "reference",
  type: "case-template",
  id: template.id,
  displayName: template.name,
});

export const mapCaseTemplateVersionToCopilotContextualEntity = (
  version: CaseTemplateVersion,
  templateId: string,
): ReferenceContextualEntity => ({
  kind: "reference",
  type: "case-template-version",
  id: version.id,
  displayName: version.attributes.version_name,
  // A version is only addressable under its template, so the entity has to
  // carry the template for the deeplink to resolve.
  parent: {
    type: "case-template",
    id: templateId,
  },
});

export const mapCaseQueueToCopilotContextualEntity = (
  queue: CaseQueueSummary,
): ReferenceContextualEntity => ({
  kind: "reference",
  type: "case-queue",
  id: queue.id,
  displayName: queue.name,
});

const toTemplateSummary = (
  templateId: string,
  templateName: string | undefined,
): CaseTemplateSummary[] =>
  templateName ? [{ id: templateId, name: templateName }] : [];

/**
 * Template versions list: the template is what the page is about, its
 * versions are offered as mentions.
 */
export const useCaseTemplateCopilotContext = ({
  templateId,
  templateName,
  versions,
}: {
  templateId: string;
  templateName: string | undefined;
  versions: CaseTemplateVersion[] | undefined;
}): void => {
  useCopilotContextualEntities(
    toTemplateSummary(templateId, templateName).map(
      mapCaseTemplateToCopilotContextualEntity,
    ),
    { includeInAgentContext: true },
  );

  useCopilotContextualEntities(
    (versions ?? []).map((version) =>
      mapCaseTemplateVersionToCopilotContextualEntity(version, templateId),
    ),
  );
};

/**
 * Template version editor: the open version and its template are what the
 * user is working on, the sibling versions are offered as mentions.
 */
export const useCaseTemplateVersionCopilotContext = ({
  templateId,
  templateName,
  versionId,
  versions,
}: {
  templateId: string;
  templateName: string | undefined;
  versionId: string;
  versions: CaseTemplateVersion[] | undefined;
}): void => {
  const currentVersion = versions?.find((version) => version.id === versionId);

  useCopilotContextualEntities(
    [
      ...toTemplateSummary(templateId, templateName).map(
        mapCaseTemplateToCopilotContextualEntity,
      ),
      ...(currentVersion
        ? [
            mapCaseTemplateVersionToCopilotContextualEntity(
              currentVersion,
              templateId,
            ),
          ]
        : []),
    ],
    { includeInAgentContext: true },
  );

  useCopilotContextualEntities(
    (versions ?? [])
      .filter((version) => version.id !== versionId)
      .map((version) =>
        mapCaseTemplateVersionToCopilotContextualEntity(version, templateId),
      ),
  );
};

/**
 * Queue list: the open queue is what the user is looking at, the workspace's
 * other queues are offered as mentions so the copilot can be pointed at one
 * without navigating there first.
 */
export const useCaseQueuesCopilotContext = ({
  queues,
  currentQueueId,
}: {
  queues: CaseQueueSummary[] | undefined;
  currentQueueId: string | undefined;
}): void => {
  const currentQueue = queues?.find((queue) => queue.id === currentQueueId);

  useCopilotContextualEntities(
    currentQueue ? [mapCaseQueueToCopilotContextualEntity(currentQueue)] : [],
    { includeInAgentContext: true },
  );

  useCopilotContextualEntities(
    (queues ?? [])
      .filter((queue) => queue.id !== currentQueueId)
      .map(mapCaseQueueToCopilotContextualEntity),
  );
};

/**
 * Queue editor: the queue being configured, absent while creating one. The
 * deserializer hands back an empty object rather than nothing when the queue
 * has not loaded, so the id is what decides there is a queue to register.
 */
export const useCaseQueueCopilotContext = (
  queue: CaseQueueSummary | undefined,
): void => {
  useCopilotContextualEntities(
    queue?.id ? [mapCaseQueueToCopilotContextualEntity(queue)] : [],
    { includeInAgentContext: true },
  );
};

/**
 * Pulls the queue and case reads back in after a copilot tool call, so a queue
 * the agent reconfigured or a case it moved shows its new state without a
 * reload. Invalidated by prefix rather than by exact key: the queue, its
 * summary and its case lists all hang off `all`, and which of them a tool
 * touched is not visible from here.
 *
 * Deliberately not mounted on the queue editor. Its react-hook-form is driven
 * by the `values` prop off the queue query, and react-hook-form resets on a
 * deep change with no `keepDirtyValues`, so an invalidation there would throw
 * away unsaved edits. Templates are covered separately by
 * `useCaseTemplatesCopilotRefetch`, which carves the open version out for the
 * same reason.
 */
export const useCaseQueuesCopilotRefetch = (workspaceId: string): void => {
  const queryClient = useQueryClient();

  useCopilotRefetchOnMutation(async () => {
    await Promise.all(
      [caseQueuesKeys.all(workspaceId), casesKeys.all(workspaceId)].map(
        (queryKey) => queryClient.invalidateQueries({ queryKey }),
      ),
    );
  });
};
